import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Table } from "antd";
import dayjs from "dayjs";
import { formatNumber } from "./chartHelpers";

const resolveUserId = (row) => row.user?.id || null;

const buildUserTrackPath = (row) => {
  const userId = resolveUserId(row);
  if (!userId) return null;
  const params = new URLSearchParams();
  if (row.distinct_id) {
    params.set("posthog_distinct_id", row.distinct_id);
  }
  const query = params.toString();
  return `/analytics-usage/users/${userId}${query ? `?${query}` : ""}`;
};

const buildUserColumns = (onNavigate) => [
  {
    title: "User",
    key: "user",
    render: (_, row) => {
      const trackPath = buildUserTrackPath(row);
      const name =
        [row.user?.first_name, row.user?.last_name].filter(Boolean).join(" ") ||
        row.user?.email ||
        null;

      if (trackPath) {
        return (
          <Link
            to={trackPath}
            className="analytics-user-link"
            onClick={() => onNavigate?.()}
          >
            {name || `User #${resolveUserId(row)}`}
          </Link>
        );
      }

      return (
        <div className="analytics-user-cell">
          {name ? <span className="analytics-user-cell__name">{name}</span> : null}
          <code className="analytics-code-sm">{row.distinct_id?.slice(0, 16)}…</code>
        </div>
      );
    },
  },
  { title: "Device", dataIndex: "device_brand", key: "brand", render: (v, r) => v || r.device || "—" },
  { title: "Platform", dataIndex: "platform", key: "platform", render: (v) => v || "—" },
  {
    title: "Location",
    key: "loc",
    render: (_, r) => [r.city, r.country].filter(Boolean).join(", ") || "—",
  },
  { title: "Events", dataIndex: "events_count", key: "ev", render: formatNumber, align: "right", width: 80 },
  {
    title: "Last seen",
    dataIndex: "last_seen",
    key: "last",
    width: 120,
    render: (v) => (v ? dayjs(v).format("D MMM HH:mm") : "—"),
  },
  {
    title: "",
    key: "track",
    width: 110,
    render: (_, row) => {
      const trackPath = buildUserTrackPath(row);
      if (!trackPath) return "—";
      return (
        <Link
          to={trackPath}
          className="analytics-view-more"
          onClick={() => onNavigate?.()}
        >
          Track <i className="fa-solid fa-arrow-right" />
        </Link>
      );
    },
  },
];

const BreakdownPills = ({ title, items, valueKey = "label" }) => {
  if (!items?.length) return null;
  return (
    <div className="analytics-breakdown-block">
      <span className="analytics-breakdown-block__label">{title}</span>
      <div className="analytics-breakdown-pills">
        {items.slice(0, 8).map((item) => (
          <span key={item[valueKey] || item.label} className="analytics-breakdown-pill">
            <span className="analytics-breakdown-pill__label">{item[valueKey] || item.label}</span>
            <strong>{formatNumber(item.users ?? item.count)}</strong>
          </span>
        ))}
      </div>
    </div>
  );
};

const WidgetDetailBreakdown = ({ audience, onNavigate }) => {
  const userColumns = useMemo(() => buildUserColumns(onNavigate), [onNavigate]);

  if (!audience) return null;

  const { breakdown, users, totals } = audience;
  const hasBreakdown =
    (breakdown?.devices?.length || 0) > 0 ||
    (breakdown?.platforms?.length || 0) > 0 ||
    (breakdown?.countries?.length || 0) > 0;

  return (
    <div className="analytics-widget-audience">
      <div className="analytics-widget-audience__stats">
        <div className="analytics-widget-audience__stat">
          <span className="analytics-widget-audience__stat-label">Unique users</span>
          <strong className="analytics-widget-audience__stat-value">{formatNumber(totals?.unique_users)}</strong>
        </div>
        <div className="analytics-widget-audience__stat">
          <span className="analytics-widget-audience__stat-label">Linked accounts</span>
          <strong className="analytics-widget-audience__stat-value">{formatNumber(totals?.matched_accounts)}</strong>
        </div>
      </div>

      {hasBreakdown && (
        <div className="analytics-widget-audience__breakdown">
          <BreakdownPills title="Devices" items={breakdown?.devices} valueKey="label" />
          <BreakdownPills title="Platforms" items={breakdown?.platforms} valueKey="label" />
          <BreakdownPills title="Countries" items={breakdown?.countries} valueKey="label" />
        </div>
      )}

      {users?.length > 0 && (
        <div className="analytics-detail-section analytics-detail-section--compact">
          <h4 className="analytics-detail-section__title">
            <i className="fa-solid fa-users" aria-hidden="true" />
            Users in this widget
          </h4>
          <div className="analytics-detail-section__body">
            <Table
              size="small"
              pagination={{ pageSize: 8, size: "small", showSizeChanger: false }}
              className="analytics-table analytics-detail-table"
              columns={userColumns}
              dataSource={users.map((r, i) => ({ ...r, key: r.distinct_id || i }))}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export { buildUserColumns, buildUserTrackPath, BreakdownPills };
export default WidgetDetailBreakdown;
